/**
 * "How we arrived at this verdict" panel — switches between three views.
 *
 *   List     — dense, text-forward; best for actually reading what each
 *              specialist said.
 *   Graph    — columnar flow (triage → specialists → final) with SVG
 *              edges for causality; best for "which triage signal
 *              triggered what?".
 *   Timeline — execution view with per-stage cost + tokens; best for
 *              economics / tuning.
 *
 * Default view is List (the densest). Choice is persisted in session
 * storage so the admin doesn't have to re-pick it every navigation.
 */
import React, {useState} from 'react';
import {Box, Paper, Tab, Tabs, Typography, Chip} from '@mui/material';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import {AgentScore, EvalAgentEntry, FeedbackLog} from '../../types/generated/graphql';
import {pcolors, cardSx, sectionLabelSx} from '../../theme/planapusTheme';
import {ScoringChainList} from './scoring-chain/ScoringChainList';
import {ScoringChainGraph} from './scoring-chain/ScoringChainGraph';
import {ScoringChainTimeline} from './scoring-chain/ScoringChainTimeline';
import {useChainData} from './scoring-chain/useChainData';

interface Props {
  scores: AgentScore[];
  agents: EvalAgentEntry[];
  childAge?: number;
  /**
   * User/admin feedback rows attached to this video. Rendered inline
   * with agent scores so a feedback-override is visually distinct from
   * an agent score. The List view pairs them by specialization and
   * shows the agent's overridden verdict with a strikethrough.
   */
  feedback?: FeedbackLog[];
}

type View = 'list' | 'graph' | 'timeline';
const STORAGE_KEY = 'scoring-chain-panel-view';

function readInitialView(): View {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === 'list' || raw === 'graph' || raw === 'timeline') return raw;
  } catch {
    // sessionStorage can throw in private-browsing / cross-origin contexts.
  }
  return 'list';
}

export function ScoringChainPanel({scores, agents, childAge = 10, feedback = []}: Props) {
  const [view, setView] = useState<View>(readInitialView);
  const {didGate, shortCircuited} = useChainData(scores, agents, childAge, feedback);

  const onChange = (_e: React.SyntheticEvent, v: View) => {
    setView(v);
    try {
      sessionStorage.setItem(STORAGE_KEY, v);
    } catch {
      // best effort — doesn't affect the view during this session
    }
  };

  return (
    <Paper sx={{...cardSx, p: 2, mb: 3}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
        <RuleFolderIcon sx={{fontSize: 18, color: pcolors.textSecondary}} />
        <Typography sx={sectionLabelSx}>How we arrived at this verdict</Typography>
        {!didGate && (
          <Chip
            size="small"
            label="legacy"
            sx={{fontSize: 10, ml: 'auto', bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
          />
        )}
        {didGate && shortCircuited && (
          <Chip
            size="small"
            label="short-circuited"
            sx={{fontSize: 10, ml: 'auto', bgcolor: pcolors.amber[50], color: pcolors.amber[700], fontWeight: 700}}
          />
        )}
      </Box>

      <Tabs
        value={view}
        onChange={onChange}
        sx={{
          minHeight: 32,
          mb: 1.5,
          borderBottom: `1px solid ${pcolors.borderLight}`,
          '& .MuiTab-root': {minHeight: 32, py: 0.5, fontSize: 12, textTransform: 'none'},
        }}
      >
        <Tab value="list" label="List" />
        <Tab value="graph" label="Graph" />
        <Tab value="timeline" label="Timeline" />
      </Tabs>

      {view === 'list' && (
        <ScoringChainList scores={scores} agents={agents} childAge={childAge} feedback={feedback} />
      )}
      {view === 'graph' && (
        <ScoringChainGraph scores={scores} agents={agents} childAge={childAge} />
      )}
      {view === 'timeline' && (
        <ScoringChainTimeline scores={scores} agents={agents} childAge={childAge} />
      )}
    </Paper>
  );
}
